import React from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'formik';
import { Button } from 'primereact/button';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import NavigationPrompt from 'react-router-navigation-prompt';
import { Dialog } from 'primereact/dialog';
import Debug from '../../../../../components/Debug';
import { renderAsyncSelector } from '../../../../../components/formik-components/FormAsyncSelector';
import { LegacyInputTextComponent, DropdownWithValidation } from '../../../../../components/prime-formik';
import FormLanguageSelector from '../../../../../components/formik-components/FormLanguageSelector';
import { GENDER_ARRAY } from '../../../../../utils/Gr8piConfig';
import { formikFormShape, intlShape } from '../../../../../components/prop-types';

const i18n = defineMessages({
  choose: {
    id: 'choose.candidate',
    defaultMessage: 'Choose candidate',
  },
  fNamePlaceholder: {
    id: 'placeholder.firstName',
    defaultMessage: 'First name',
  },
  lNamePlaceholder: {
    id: 'placeholder.lastName',
    defaultMessage: 'Last name',
  },
  emailPlaceholder: {
    id: 'placeholder.email',
    defaultMessage: 'Email',
  },
  genderPlaceHolder: {
    id: 'actions.selectGender',
    defaultMessage: '- Select gender -',
  },
  localePlaceHolder: {
    id: 'placeholder.language',
    defaultMessage: '- Select language -',
  },
  gender_M: {
    id: 'attrs.gender.M',
    defaultMessage: 'Male',
  },
  gender_F: {
    id: 'attrs.gender.F',
    defaultMessage: 'Female',
  },
  gender_U: {
    id: 'attrs.gender.U',
    defaultMessage: 'Unspecified',
  },
  formattedName: {
    id: 'formattedNaming',
    defaultMessage: '{firstName} {lastName}',
  },
  add: { id: 'actions.add', defaultMessage: 'Add' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  reset: { id: 'actions.reset', defaultMessage: 'Reset' },
  yes: { id: 'btn.lbl.yes', defaultMessage: 'Yes' },
  no: { id: 'btn.lbl.no', defaultMessage: 'No' },
  header: { id: 'modal.navigation.away.header', defaultMessage: 'Unsaved Changes' },
  warning: {
    id: 'modal.navigation.away.warning',
    defaultMessage: 'Are you sure you want to leave this page? You have unsaved changes.',
  },
});

const footer = (onConfirm, onCancel, formatMessage) => (
  <div>
    <Button label={formatMessage(i18n.yes)} icon="pi pi-check" onClick={onConfirm} />
    <Button label={formatMessage(i18n.no)} icon="pi pi-times" onClick={onCancel} className="p-button-secondary" />
  </div>
);

const parseNameAndEmail = (first = '', last = '', uEmail = '') => `${first} ${last} (${uEmail})`;

const HintsJPSAddForm = (props) => {
  const {
    formProps: {
      isSubmitting, setFieldValue,
      values, resetForm,
    },
    intl: { formatMessage },
    onCancel,
    respondentList, loadingRespondents, searchRespondent,
    jpsRespondents,
    activeLocales,
  } = props;
  const genderOptions = GENDER_ARRAY.map(key => ({
    value: key,
    label: formatMessage(i18n[`gender_${key}`]),
  }));
  const addExisting = (values && values.respondent);

  return (
    <>
      <NavigationPrompt
        renderIfNotActive
        when={
          (crntLocation, nextLocation) => (!nextLocation
            || !nextLocation.pathname.startsWith(crntLocation.pathname))
        }
      >
        {({ isActive, onCancel: onCancelNavigation, onConfirm }) => {
          if (isActive) {
            return (
              <Dialog
                header={formatMessage(i18n.header)}
                visible={isActive}
                style={{ width: '50vw' }}
                modal
                footer={footer(onConfirm, onCancelNavigation, formatMessage)}
                onHide={() => {
                  onCancelNavigation();
                }}
              >
                {formatMessage(i18n.warning)}
              </Dialog>
            );
          }
          return null;
        }}
      </NavigationPrompt>
      <Form>
        <div className="p-grid">
          <div className="p-col">
            <Field
              id="respondent"
              name="respondent"
              component={renderAsyncSelector}
              processing={loadingRespondents}
              data={respondentList}
              filter
              valueField="id"
              textField={item => (item ? parseNameAndEmail(item.firstName, item.lastName, item.email) : '')}
              onSearch={searchRespondent}
              selectedList={jpsRespondents}
              setFieldValue={setFieldValue}
              placeholder={`- ${formatMessage(i18n.choose)} -`}
            />
          </div>
        </div>
        <div className="p-grid">
          <div className="p-col field-separator">
            <FormattedMessage id="or" defaultMessage="OR" />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-md-6 p-sm-12">
            <Field
              component={LegacyInputTextComponent}
              name="firstName"
              id="firstName"
              placeholder={formatMessage(i18n.fNamePlaceholder)}
              disabled={!!addExisting}
            />
          </div>
          <div className="p-md-6 p-sm-12">
            <Field
              component={LegacyInputTextComponent}
              name="lastName"
              id="lastName"
              placeholder={formatMessage(i18n.lNamePlaceholder)}
              disabled={!!addExisting}
            />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col">
            <Field
              component={LegacyInputTextComponent}
              name="email"
              id="email"
              placeholder={formatMessage(i18n.emailPlaceholder)}
              disabled={!!addExisting}
            />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col">
            <Field
              id="locale"
              name="locale"
              component={FormLanguageSelector}
              options={activeLocales}
              placeholder={formatMessage(i18n.localePlaceHolder)}
              disabled={!!addExisting}
            />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col">
            <Field
              id="gender"
              name="gender"
              component={DropdownWithValidation}
              options={genderOptions}
              placeholder={`${formatMessage(i18n.genderPlaceHolder)}`}
              disabled={!!addExisting}
            />
          </div>
        </div>
        <div className="p-grid">
          <div className="p-col">
            <Button
              type="submit"
              icon={isSubmitting ? 'pi pi-spin pi-spinner' : ''}
              label={formatMessage(i18n.add)}
              disabled={isSubmitting}
            />

            <Button
              className="p-button-warning"
              type="button"
              label={formatMessage(i18n.reset)}
              onClick={() => {
                resetForm();
              }}
            />

            <Button
              className="p-button-secondary"
              type="button"
              label={formatMessage(i18n.cancel)}
              onClick={onCancel}
            />
          </div>
        </div>
        <Debug />
      </Form>
    </>
  );
};

HintsJPSAddForm.propTypes = {
  formProps: formikFormShape.isRequired,
  intl: intlShape.isRequired,
  onCancel: PropTypes.func.isRequired,
  loadingRespondents: PropTypes.bool.isRequired,
  searchRespondent: PropTypes.func.isRequired,
  respondentList: PropTypes.arrayOf(PropTypes.any).isRequired,
  jpsRespondents: PropTypes.arrayOf(PropTypes.any).isRequired,
  activeLocales: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default injectIntl(
  HintsJPSAddForm,
);
